
import { Link, useLocation } from "react-router-dom";
import { NavItem } from "@/types/layout";
import { 
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ChevronDown } from "lucide-react";

interface NavigationProps {
  navItems: NavItem[];
  isMobile?: boolean;
  onItemClick?: () => void;
}

const Navigation = ({ navItems, isMobile = false, onItemClick }: NavigationProps) => {
  const location = useLocation();
  
  const renderNavItem = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    const baseClasses = isMobile
      ? "block px-4 py-2 rounded-md text-base font-medium"
      : "px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200";
    
    const activeClasses = isActive
      ? "bg-primary/10 text-primary"
      : "text-foreground/70 hover:text-foreground hover:bg-accent/20";
      
    // For items with dropdown
    if (item.dropdown && !isMobile) {
      return (
        <HoverCard openDelay={100} closeDelay={200}>
          <HoverCardTrigger asChild>
            <div className={`${baseClasses} ${activeClasses} flex items-center cursor-pointer`}>
              {item.name}
              <ChevronDown size={16} className="ml-1" />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-auto p-0">
            <div className="py-2">
              {item.dropdown.map((dropdownItem) => (
                <Link
                  key={dropdownItem.path}
                  to={dropdownItem.path}
                  className="block px-4 py-2 text-sm hover:bg-accent/20"
                  onClick={onItemClick}
                >
                  {dropdownItem.name}
                </Link>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    }
    
    // For mobile items with dropdown
    if (item.dropdown && isMobile) {
      return (
        <>
          <div className={`${baseClasses} ${activeClasses}`}>
            {item.name}
          </div>
          <div className="pl-4 space-y-1">
            {item.dropdown.map((dropdownItem) => (
              <Link
                key={dropdownItem.path}
                to={dropdownItem.path}
                className="block px-4 py-2 text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-accent/20 rounded-md"
                onClick={onItemClick}
              >
                {dropdownItem.name}
              </Link>
            ))}
          </div>
        </>
      );
    }
    
    // For regular items
    return (
      <Link
        to={item.path}
        className={`${baseClasses} ${activeClasses}`}
        onClick={onItemClick}
      >
        {item.name}
      </Link>
    );
  };
  
  if (isMobile) {
    return (
      <div className="md:hidden animate-fade-in">
        <div className="px-4 py-3 space-y-1">
          {navItems.map((item) => (
            <div key={item.path}>
              {renderNavItem(item)}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <nav className="hidden md:flex items-center space-x-1">
      {navItems.map((item) => (
        <div key={item.path}>
          {renderNavItem(item)}
        </div>
      ))}
    </nav>
  );
};

export default Navigation;
